'use client';

import { Button } from '@heroui/button';
import {
  Smartphone,
  Clock,
  Headphones,
  TrendingUp,
  Building,
  UserPlus,
  Wallet,
  Users,
  ArrowUpDown,
  DollarSign,
} from 'lucide-react';
import Link from 'next/link';

import InteractiveMap from '@/components/common/agent-network-map';
import { Feature } from '@/components/common/feature-variants';

const steps = [
  {
    title: 'Sign Up as an Agent',
    icon: UserPlus,
    description:
      'Create your account through the PayStop app by providing your business information.',
  },
  {
    title: 'Fund Your Account',
    icon: Wallet,
    description:
      'Fund your PayStop account through a commercial bank to offer deposit and withdrawal services.',
  },
  {
    title: 'Invite Sub-Agents',
    icon: Users,
    description:
      'Super Agents can invite Sub-Agents to extend their reach and serve more customers in different locations.',
  },
  {
    title: 'Start Offering Services',
    icon: ArrowUpDown,
    description:
      'Begin offering deposits, withdrawals, and transfers to customers using the PayStop platform.',
  },
  {
    title: 'Earn Transaction-Based Rewards',
    icon: DollarSign,
    description:
      'Earn rewards based on a percentage of the transaction fee for each service you provide.',
  },
];

export default function AgentNetworkPage() {
  return (
    <>
      <section className="text-center section-md bg-gradient-to-b from-primary/20 to-background/5">
        <div className="container space-y-6 container-max ">
          <h1 className="mb-6 text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary ">
            <span> PayStop Agent Network</span>
          </h1>
          <p className="max-w-3xl mx-auto leading-relaxed text-foreground/70">
            Join our network of trusted professionals and empower your community with essential
            financial services. Become a key player in the financial ecosystem while growing your
            business.
          </p>
          <div>
            <Link href="/become-agent">
              <Button color="primary" size="lg">
                Become an Agent
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <section className=" section-md">
        <div className="container space-y-12 container-max ">
          <h2 className="font-semibold text-center title">Why Become a PayStop Agent?</h2>
          <div className="grid gap-20 md:grid-cols-3">
            {[
              {
                title: 'Earn Competitive Rewards',
                icon: DollarSign,
                description:
                  'Earn a percentage of transaction fees for every deposit, withdrawal, and money transfer made through your service point.',
              },
              {
                title: 'Access Digital Banking Platform',
                icon: Smartphone,
                description:
                  'Connect to digital banking, mobile wallets, and traditional bank accounts to serve a broad range of customers.',
              },
              {
                title: 'Easy to Start',
                icon: UserPlus,
                description:
                  'Simple setup process with just a PayStop account and an initial deposit through a commercial bank.',
              },
              {
                title: 'Flexible Service Hours',
                icon: Clock,
                description:
                  'Offer services at your convenience, whether you run a retail shop, kiosk, or mobile service.',
              },
              {
                title: 'Support and Training',
                icon: Headphones,
                description:
                  'Receive comprehensive support from onboarding and training to ongoing customer service and technical assistance.',
              },
              {
                title: 'Expand Your Business',
                icon: TrendingUp,
                description:
                  'Grow your business by offering essential financial services to your community.',
              },
            ].map((item) => (
              <Feature
                key={item.title} // Use the `title` as a unique key here
                description={item.description}
                icon={<item.icon className="w-6 h-6 text-primary" />}
                title={item.title}
                variant="horizontal"
              />
            ))}
          </div>
        </div>
      </section>
      <section className="section-md">
        <div className="container space-y-12 container-min">
          <h2 className="text-center title">How the Agent Network Works</h2>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-primary/20" />
            <div className="space-y-12">
              {steps.map((step, index) => (
                <div key={step.title} className="relative">
                  <Feature
                    description={step.description}
                    icon={<step.icon className="w-6 h-6 text-primary-foreground" />}
                    title={step.title}
                    variant="timeline"
                  />
                  {index < steps.length - 1 && (
                    <div className="absolute left-6  h-fulle top-12 bottom-0 w-0.5 bg-primary/20" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className=" section-md">
        <div className="container space-y-12 container-max ">
          <h2 className="font-semibold text-center title">Agent Types</h2>
          <div className="grid gap-8 md:grid-cols-3">
            {[
              {
                title: 'Super Agents',
                icon: Users,
                description:
                  'Top-tier agents who manage their network of agents, invite Sub-Agents, set transaction limits, and oversee operations.',
              },
              {
                title: 'Agent Admin',
                icon: Building,
                description:
                  'Works under the Super Agent, managing day-to-day transactions and ensuring smooth operations.',
              },
              {
                title: 'Sub-Agents',
                icon: Smartphone,
                description:
                  "Part of the Super Agent's network, typically working at street-level kiosks or shops to offer services to local customers.",
              },
            ].map((item) => (
              <Feature
                key={item.title} // Use the `title` as a unique key here
                description={item.description}
                icon={<item.icon className="w-6 h-6 text-primary" />}
                title={item.title}
                variant="card"
              />
            ))}
          </div>
        </div>
      </section>
      {/* <section className="text-center section-md">
        <div className="container space-y-12 container-min">
          <h1 className="mb-6 title ">
            <span> PayStop Agent Network Map</span>
          </h1>
          <InteractiveMap />
        </div>
      </section> */}

      <section className="section-md">
        <div className="container space-y-12 container-md">
          <h2 className="font-semibold text-center title">Agent Requirements</h2>
          <div className="grid gap-20 md:grid-cols-2">
            {[
              {
                title: 'Established Business',
                icon: Building,
                description:
                  'Operate an established retail or mobile-based business to ensure a trustworthy presence in your community.',
              },
              {
                title: 'Initial Funding',
                icon: Wallet,
                description:
                  'Fund your account through a commercial bank to offer deposit and withdrawal services.',
              },
              {
                title: 'Basic Equipment',
                icon: Smartphone,
                description:
                  'Have necessary equipment like a smartphone or tablet with internet access for transactions.',
              },
              {
                title: 'Customer Service Commitment',
                icon: Headphones,
                description:
                  'Provide excellent customer service and ensure secure, professional transactions.',
              },
            ].map((item) => (
              <Feature
                key={item.title} // Use the `title` as a unique key here
                description={item.description}
                icon={<item.icon className="w-6 h-6 text-primary" />}
                title={item.title}
                variant="horizontal"
              />
            ))}
          </div>
        </div>
      </section>
      <section className="text-center section-md">
        <div className="container space-y-12 container-max ">
          <h2 className="font-semibold title">Become a PayStop Agent Today</h2>
          <p className="max-w-3xl mx-auto text-foreground/70">
            Join the PayStop Agent Network today and start offering financial services to your
            community while earning rewards for every transaction. We make it easy to get started,
            and our team is always here to support you along the way.
          </p>
          <div className="flex flex-col items-center justify-center space-y-4 md:flex-row md:space-y-0 md:space-x-8">
            <Button className="px-8 py-4 " color="primary" size="lg">
              <Link href="become-agent">Sign Up</Link>
            </Button>
            <div className="md:text-left">
              <p className="font-semibold">Contact Us</p>
              <p className="text-foreground/70">Email: support@paystop.io</p>
              <p className="text-foreground/70">Phone: +237 676962723</p>
            </div>
          </div>
        </div>
      </section>
      <section className="relative overflow-hidden section-md bg-gradient-to-r from-primary/20 to-primary/5 ">
        <div className="container space-y-6 container-max ">
          <div className="relative z-10 space-y-6 text-center">
            <h2 className="font-semibold title">Grow Your Business with PayStop</h2>
            <p className="max-w-3xl mx-auto text-sm text-foreground/70">
              At PayStop, we are transforming the way people access financial services. By becoming
              an agent, you can offer a wide range of services that make a difference in your
              community while expanding your business and earning rewards. Don&apos;t miss out on
              the opportunity to be part of this exciting network.
            </p>
            <Button className="px-8 py-4 " color="primary" size="lg">
              <Link href="become-agent">Join Our Network</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
